import JwtBaseClient from '@/api/JwtBaseClient';
import store from '@/store';
import STATE_SORT_OPTIONS_QUERY from '@/api/graphql/definitions/queries/stateSortOptionsQuery';
import GaiaGraphQLClient from '@/api/graphql/GaiaGraphQLClient';

/**
 * @class SearchServiceClient
 *
 * @description API wrapper for Search Service API
 */
class SearchServiceClient {
  constructor() {
    this.jwtBaseClient = new JwtBaseClient(
      `${import.meta.env.VITE_SEARCH_SERVICE_API_URL}api/`,
      null,
      {
        'json-naming-strategy': 'camel',
      },
    );
  }

  /**
   * @function getFilterOptions
   * @description Returns filter options for the front end
   * @returns {Promise}
   */
  getFilterOptions() {
    return this.jwtBaseClient.get('filters/filter_options');
  }

  /**
   * @function getFilters
   * @description Returns filters based on a given query
   * @returns {Promise}
   */
  getFilters(query) {
    return this.jwtBaseClient.get('search/filters', { query });
  }

  /**
   * @function getTrimOptions
   * @description Returns trim options for the front end
   * @returns {Promise}
   */
  getTrimOptions(params) {
    return this.jwtBaseClient.get('filters/trim_options', params);
  }

  /**
   * @function getConditionOptions
   * @description Returns Condition report options for the front end
   * @returns {Promise}
   */
  getConditionOptions() {
    return this.jwtBaseClient.get('filters/condition_options');
  }

  /**
   * @function saveSavedSearch
   * @description Creates a saved search
   * @returns {Promise}
   */
  saveSavedSearch(savedSearch) {
    return this.jwtBaseClient.post('saved_search', savedSearch);
  }

  /**
   * @function updateSavedSearch
   * @description Updates a saved search
   * @returns {Promise}
   */
  updateSavedSearch(savedSearch) {
    return this.jwtBaseClient.put(`saved_search/${savedSearch.id}`, savedSearch);
  }

  /**
   * @function deleteSavedSearch
   * @description Deletes a saved search
   * @returns {Promise}
   */
  deleteSavedSearch(savedSearchId) {
    return this.jwtBaseClient.delete(`saved_search/${savedSearchId}`);
  }

  /**
   * @function getSavedSearches
   * @description Gets all saved searches
   * @returns {Promise}
   */
  getSavedSearches() {
    return this.jwtBaseClient.get('saved_searches');
  }

  /**
   * @function getSavedSearch
   * @description Gets a saved search
   * @returns {Promise}
   */
  getSavedSearch(savedSearchId) {
    return this.jwtBaseClient.get(`saved_search/${savedSearchId}`);
  }

  /**
   * @function saveRecentSearch
   * @description Returns saved recent search
   * @returns {Promise}
   */
  saveRecentSearch(filters) {
    return this.jwtBaseClient.post('recent-search/create', { filters });
  }

  /**
   * @function getAuctionEndedCountsMatchingFilter
   * @description Returns a count of ended auctions matching the filters
   * @returns {Promise}
   */
  getAuctionEndedCountsMatchingFilter(filterData) {
    return this.jwtBaseClient.post('search/ended', filterData);
  }

  /**
   * @function getSearchSuggestions
   * @description Returns a collection of filterTypes
   * @returns {Promise}
   */
  getSearchSuggestions(query) {
    return this.jwtBaseClient.get('search/suggestions', { query });
  }

  /**
   * @function addWatchlist
   * @description Returns a collection of filterTypes
   * @returns {Promise}
   */
  addWatchlist(savedAuctionId) {
    return this.jwtBaseClient.post(`watchlist/saved_auction/${savedAuctionId}`);
  }

  /**
   * @function removeWatchlist
   * @description Returns a collection of filterTypes
   * @returns {Promise}
   */
  removeWatchlist(savedAuctionId) {
    return this.jwtBaseClient.delete(`watchlist/saved_auction/${savedAuctionId}`);
  }

  /**
   * @function getStatesAndSorts
   * @description Returns a collection of states and sort options for Marketplace
   * @returns {Promise}
   */
  async getStatesAndSorts() {
    const usingGraphQl = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.enableGraphqlOnSrp,
    );

    if (usingGraphQl) {
      const apiResponse = await GaiaGraphQLClient().query({
        operation: {
          query: STATE_SORT_OPTIONS_QUERY,
        },
      });

      return apiResponse.data?.stateSortOptions;
    }

    const response = await this.jwtBaseClient.get('filters/states_and_sorts');
    return response.data?.data;
  }
}

export default SearchServiceClient;
